/**
 * Types TypeScript partagés pour la gestion des garanties
 * Utilisés par admin et outil-pro
 */

/**
 * Interface complète d'une garantie
 * Mapping de la table PRO_WARRANTY
 */
export interface Warranty {
  uuid: string;
  idWarranty: number;
  code: string;
  warrantyType: number; // 0=Garantie, 1=Service
  name: string;
  duration: number; // En mois: 3, 6, 12, 24, 36, 48, 60
  kmLimit: number | null;
  financialCap: number;
  sellingPrice: number;
  isBroadcast: boolean;
  isActive: boolean;
  societyId: string;
  provision: number | null;
  commission: number | null;
  commissionType: number | null; // 1=montant fixe, 2=pourcentage
  movement: any | null;
  type: any | null;
  complication: any | null;
  template: string | null;
  diagnostic: string | null;
  headTransferPrice: number | null;
  memberTransferPrice: number | null;
  vehicleType: number; // 0=Standard, 1=Collection, 2=Deux-roues
  servicePrice: number;
  /** Formule d'assistance retenue (`null` = aucune). */
  assistanceFormulaId: number | null;
  /** Supplément assistance HT. S'ADDITIONNE à `servicePrice`, ne le remplace pas. */
  assistancePrice: number | null;
  isMensualisable: boolean;
  isCashPayment: boolean;
  createdAt?: string;
  updatedAt?: string | null;
  color?: string | null;
  order?: number | null;
  companyContractCode?: string;

  /**
   * FK explicite vers une version de CG pour les contrats payés en cash.
   * NULL = défaut Affinicar.
   */
  v2GeneralConditionsIdCash?: string | null;

  /**
   * FK explicite vers une version de CG pour les contrats payés en Stripe.
   * NULL = défaut Affinicar.
   */
  v2GeneralConditionsIdStripe?: string | null;

  // Relations optionnelles
  parts?: any[];
  society?: {
    uuid: string;
    name: string;
    code: string;
    isGroup: number;
  };
}

/**
 * Résultat de la propagation des "Informations complémentaires"
 * d'une garantie source vers toutes les autres garanties de la société.
 */
export interface PropagateDiagnosticResult {
  sourceWarrantyUuid: string;
  societyId: string;
  diagnostic: string | null;
  updatedCount: number;
  updatedUuids: string[];
}

/**
 * Interface pour la liste des garanties (optimisée avec JOIN)
 */
export interface WarrantyListItem {
  uuid: string;
  code: string;
  name: string;
  warrantyType: number;
  vehicleType: number;
  duration: number;
  kmLimit: number | null;
  financialCap: number;
  sellingPrice: number;
  servicePrice: number;
  /** Formule d'assistance retenue (`null` = aucune). */
  assistanceFormulaId: number | null;
  /** Supplément assistance HT, à additionner à `servicePrice`. */
  assistancePrice: number | null;
  commission: number | null;
  commissionType: number | null;
  isActive: boolean;
  isBroadcast: boolean;
  isMensualisable: boolean;
  isCashPayment: boolean;
  createdAt: string;
  updatedAt?: string;
  color?: string | null;
  order?: number | null;
  // Société (via JOIN)
  societyId: string;
  societyName: string;
  societyCode: string;
  societyType: string;
  monthlyPlateformFees: number;
  // Stats
  partsCount: number;
  contractsCount?: number;
  // FKs CG (cash / stripe)
  v2GeneralConditionsIdCash?: string | null;
  v2GeneralConditionsIdStripe?: string | null;
}

/**
 * Filtres de recherche des garanties
 */
export interface WarrantyFilters {
  nameFilter?: string;
  societyId?: string;
  warrantyType?: number;
  vehicleType?: number;
  isActive?: boolean;
  isBroadcast?: boolean;
  /**
   * Présence d'une formule d'assistance. Filtré côté SERVEUR : la liste est
   * paginée (20 par page), un filtre client ne porterait que sur la page
   * affichée et fausserait le compteur total. Absent = pas de filtre.
   */
  hasAssistance?: boolean;
  /**
   * `true` : exclut les garanties réservées à l'upsell (non proposées dans
   * l'outil pro). Forcé à `true` côté API pour les utilisateurs externes ; à
   * passer explicitement pour qu'un interne qui crée un contrat ne les voie pas.
   */
  visibleInPro?: boolean;
  durationFilter?: number;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

/**
 * Réponse de la liste des garanties avec pagination
 */
export interface WarrantyListResponse {
  warranties: WarrantyListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Informations de pricing calculées
 */
export interface WarrantyPricing {
  sellingPriceTTC: number;
  sellingPriceHT: number;
  servicePriceHT: number;
  headTransferPrice: number | null;
  memberTransferPrice: number | null;
  provision: number | null;
  commission: number | null;
  calculatedRevenue: number;
  displayLabel: string;
  displayValue: number;
}

// ==================== CONSTANTES ====================

export const WARRANTY_TYPES = {
  WARRANTY: 0,
  SERVICE: 1,
  EXTENSION: 2,
} as const;

export const WARRANTY_TYPE_LABELS: Record<number, string> = {
  [WARRANTY_TYPES.WARRANTY]: 'Garantie',
  [WARRANTY_TYPES.SERVICE]: 'Prestation',
  [WARRANTY_TYPES.EXTENSION]: 'Extension de garantie constructeur',
};

export const VEHICLE_TYPES = {
  STANDARD: 0,
  COLLECTION: 1,
  TWO_WHEELER: 2,
} as const;

export const VEHICLE_TYPE_LABELS: Record<number, string> = {
  [VEHICLE_TYPES.STANDARD]: 'Standard',
  [VEHICLE_TYPES.COLLECTION]: 'Collection',
  [VEHICLE_TYPES.TWO_WHEELER]: 'Deux-roues',
};

export const BASE_DURATION_OPTIONS = [3, 6, 12, 24] as const;
export const EXTENDED_DURATION_OPTIONS = [36, 48, 60] as const;
export const DURATION_OPTIONS = [...BASE_DURATION_OPTIONS, ...EXTENDED_DURATION_OPTIONS] as const;

export type DurationOption = typeof DURATION_OPTIONS[number] | 1;

export const DURATION_LABELS: Record<DurationOption, string> = {
  1: '1 mois',
  3: '3 mois',
  6: '6 mois',
  12: '12 mois',
  24: '24 mois',
  36: '36 mois',
  48: '48 mois',
  60: '60 mois',
};

export const PART_SELECTION_PRIORITY = {
  ESSENTIAL: 1,
  RECOMMENDED: 2,
  OPTIONAL: 3,
} as const;

export const WARRANTY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DRAFT: 'draft',
} as const;

export const WARRANTY_CODE_FORMAT = /^GRT_[A-Z0-9]+_[A-Z0-9]+$/;

export const PRICE_LIMITS = {
  MIN: 0,
  MAX: 100000,
} as const;

export const DEFAULT_KM_LIMITS: Record<DurationOption, number> = {
  1: 0,
  3: 5000,
  6: 10000,
  12: 20000,
  24: 40000,
  36: 60000,
  48: 80000,
  60: 100000,
};

/**
 * DTO pour la création d'une garantie
 */
export interface CreateWarrantyDto {
  societyId: string;
  name: string;
  warrantyType: number;
  duration: number;
  sellingPrice: number;
  financialCap: number;
  vehicleType: number;
  /**
   * Prix de la prestation Affinicar HT. Facultatif : réservé aux utilisateurs
   * internes, il est ignoré côté API pour un utilisateur externe, qui se voit
   * appliquer le tarif résolu (propre → parent facturant → catalogue).
   */
  servicePrice?: number;
  /** Formule d'assistance retenue (`null` = aucune). */
  assistanceFormulaId?: number | null;
  kmLimit?: number | null;
  provision?: number | null;
  commission?: number | null;
  commissionType?: number | null;
  headTransferPrice?: number | null;
  memberTransferPrice?: number | null;
  diagnostic?: string | null;
  isMensualisable?: boolean;
  isCashPayment?: boolean;
  isBroadcast?: boolean;
  isActive?: boolean;
  partIds?: number[];
  color?: string | null;
  order?: number | null;
  movement?: any;
  type?: any;
  complication?: any;
}
